import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, MapPin, AlignLeft, CalendarDays, Plus, Trash2, Loader2, Video, Users, UserPlus } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import SelectCustomerModal from './manage/SelectCustomerModal';
import type { Customer } from '../lib/customers';
import type { GoogleCalendarEventPrefill } from '../lib/crm/googleCalendarEventPrefill';

interface CreateGoogleCalendarEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialDate?: Date | null;
  /** Optional range end from calendar multi-day / multi-slot selection. */
  initialEndDate?: Date | null;
  /** CRM opportunity / calendar context — applied when the modal opens. */
  prefill?: GoogleCalendarEventPrefill | null;
  businessId: string;
  syncId?: string | null;
  /** The team member whose column was clicked. Left out, the event goes under whoever is signed in. */
  resourceId?: string | null;
  onSuccess?: () => void;
}

function applyDateRangeToFields(
  start: Date,
  end: Date | null | undefined,
  setters: {
    setStartDate: (v: string) => void;
    setEndDate: (v: string) => void;
    setStartTime: (v: string) => void;
    setEndTime: (v: string) => void;
  }
) {
  setters.setStartDate(format(start, 'yyyy-MM-dd'));
  setters.setStartTime(format(start, 'HH:mm'));

  const hasRangeEnd =
    end != null && !Number.isNaN(end.getTime()) && end.getTime() > start.getTime();
  const endToUse = hasRangeEnd ? end! : (() => {
    const d = new Date(start);
    d.setHours(d.getHours() + 1);
    return d;
  })();

  setters.setEndDate(format(endToUse, 'yyyy-MM-dd'));
  setters.setEndTime(format(endToUse, 'HH:mm'));
}

export default function CreateGoogleCalendarEventModal({
  isOpen,
  onClose,
  initialDate,
  initialEndDate = null,
  prefill,
  businessId,
  syncId,
  resourceId,
  onSuccess,
}: CreateGoogleCalendarEventModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('10:00');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [addConference, setAddConference] = useState(false);
  const [attendees, setAttendees] = useState<string[]>([]);
  const [attendeeNames, setAttendeeNames] = useState<Record<string, string>>({});
  const [newAttendeeEmail, setNewAttendeeEmail] = useState('');
  const [isSelectCustomerOpen, setIsSelectCustomerOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsVisible(true));
      });
      if (prefill?.title) setTitle(prefill.title);
      if (prefill?.location) setLocation(prefill.location);
      if (prefill?.description) setDescription(prefill.description);

      const dateForFields = prefill?.initialDate ?? initialDate;
      if (dateForFields) {
        applyDateRangeToFields(dateForFields, initialEndDate, {
          setStartDate,
          setEndDate,
          setStartTime,
          setEndTime,
        });
      }

      if (prefill?.attendeeEmail) {
        const email = prefill.attendeeEmail.trim().toLowerCase();
        if (email) {
          setAttendees([email]);
          setAttendeeNames(
            prefill.attendeeName?.trim()
              ? { [email]: prefill.attendeeName.trim() }
              : {},
          );
        }
      }
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setTitle('');
        setStartDate('');
        setStartTime('09:00');
        setEndDate('');
        setEndTime('10:00');
        setLocation('');
        setDescription('');
        setAddConference(false);
        setAttendees([]);
        setAttendeeNames({});
        setNewAttendeeEmail('');
        setIsSelectCustomerOpen(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, initialDate, initialEndDate, prefill]);

  const handleAddAttendee = () => {
    const email = newAttendeeEmail.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (attendees.includes(email)) {
      toast.error('This email is already added');
      return;
    }
    setAttendees(prev => [...prev, email]);
    setNewAttendeeEmail('');
  };

  const handleRemoveAttendee = (email: string) => {
    setAttendees(prev => prev.filter(a => a !== email));
    setAttendeeNames(prev => {
      const next = { ...prev };
      delete next[email];
      return next;
    });
  };

  const handleSelectCustomer = (customer: Customer) => {
    setIsSelectCustomerOpen(false);
    const email = customer.email?.trim().toLowerCase();
    if (!email) {
      toast.error('This customer has no email address');
      return;
    }
    if (attendees.includes(email)) {
      toast.error('This customer is already added');
      return;
    }
    const name = `${customer.first_name} ${customer.last_name}`.trim();
    setAttendees(prev => [...prev, email]);
    if (name) {
      setAttendeeNames(prev => ({ ...prev, [email]: name }));
    }
  };

  const handleCreate = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!startDate || !startTime) {
      toast.error('Please set a start date and time');
      return;
    }
    if (endDate && endTime && startDate && startTime) {
      const start = new Date(`${startDate}T${startTime}`);
      const end = new Date(`${endDate}T${endTime}`);
      if (end <= start) {
        toast.error('End time must be after start time');
        return;
      }
    }

    // Auto-add any email typed in the input but not yet explicitly added
    const finalAttendees = [...attendees];
    const pendingEmail = newAttendeeEmail.trim().toLowerCase();
    if (pendingEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pendingEmail) && !finalAttendees.includes(pendingEmail)) {
      finalAttendees.push(pendingEmail);
    }

    setIsSaving(true);
    try {
      const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const guestEmails = finalAttendees.length > 0 ? finalAttendees : undefined;
      console.log('[GCal Create] Submitting with attendees:', guestEmails, 'timezone:', browserTimeZone);

      const { data, error } = await supabase.functions.invoke('gcal-event-create', {
        body: {
          businessId,
          syncId: syncId || undefined,
          resourceId: resourceId || undefined,
          title: title.trim(),
          startDate,
          startTime,
          endDate: endDate || startDate,
          endTime: endTime || startTime,
          timeZone: browserTimeZone,
          location: location.trim() || undefined,
          description: description.trim() || undefined,
          attendees: guestEmails,
          addConference,
        },
      });

      if (error) {
        // Try to extract the response body for a better error message
        const ctx = (error as any)?.context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const errBody = await ctx.json();
            throw new Error(errBody?.error || errBody?.details || error.message);
          } catch (_) { /* fall through */ }
        }
        throw error;
      }
      if (data?.error) throw new Error(data.error);

      toast.success('Google Calendar event created');
      onClose();
      onSuccess?.();
    } catch (err: any) {
      console.error('Failed to create Google Calendar event:', err);
      toast.error(err.message || 'Failed to create event');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isAnimating && !isOpen) return null;

  const inputClasses = 'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all bg-white';

  return createPortal(
    <>
      <div
        className="fixed inset-0 transition-opacity duration-300"
        style={{
          zIndex: 100200,
          background: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          opacity: isVisible ? 1 : 0,
          pointerEvents: isVisible ? 'auto' : 'none',
        }}
        onClick={onClose}
      />

      <div
        className="fixed top-0 right-0 h-full flex items-stretch pointer-events-none"
        style={{ zIndex: 100210, width: '100%', maxWidth: 480 }}
      >
        <div
          className="w-full bg-white shadow-2xl pointer-events-auto flex flex-col transition-transform duration-300 ease-out"
          style={{
            transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
            borderTopLeftRadius: 20,
            borderBottomLeftRadius: 20,
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100" style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center">
                <CalendarDays className="h-4 w-4 text-white" />
              </div>
              <h2 className="text-base font-semibold text-gray-900">New Google Calendar Event</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-4.5 w-4.5 text-gray-400" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {/* Title */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Title</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Add title"
                className={inputClasses}
                autoFocus
              />
            </div>

            {/* Date & Time */}
            <div className="bg-gray-50/80 rounded-xl p-3.5 space-y-3">
              <div className="flex items-center gap-2 text-xs font-medium text-gray-500 mb-1">
                <Clock className="h-3.5 w-3.5" />
                <span>Date & Time</span>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => {
                      const newStartDate = e.target.value;
                      setStartDate(newStartDate);
                      const s = new Date(`${newStartDate}T${startTime || '09:00'}`);
                      if (!isNaN(s.getTime())) {
                        const end = new Date(s.getTime() + 60 * 60 * 1000);
                        setEndDate(format(end, 'yyyy-MM-dd'));
                        setEndTime(format(end, 'HH:mm'));
                      }
                    }}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={e => {
                      const newStartTime = e.target.value;
                      setStartTime(newStartTime);
                      const dateStr = startDate || format(new Date(), 'yyyy-MM-dd');
                      const s = new Date(`${dateStr}T${newStartTime}`);
                      if (!isNaN(s.getTime())) {
                        const end = new Date(s.getTime() + 60 * 60 * 1000);
                        setEndDate(format(end, 'yyyy-MM-dd'));
                        setEndTime(format(end, 'HH:mm'));
                      }
                    }}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    min={startDate}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">End Time</label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={e => setEndTime(e.target.value)}
                    className={inputClasses}
                  />
                </div>
              </div>
            </div>

            {/* Google Meet Toggle */}
            <div className="flex items-center justify-between bg-gray-50/80 rounded-xl px-3.5 py-3">
              <div className="flex items-center gap-2.5">
                <Video className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-gray-700">Add Google Meet video conferencing</span>
              </div>
              <button
                type="button"
                onClick={() => setAddConference(!addConference)}
                className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${addConference ? 'bg-blue-500' : 'bg-gray-300'}`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${addConference ? 'translate-x-5' : ''}`}
                />
              </button>
            </div>

            {/* Location */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                <div className="flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" />
                  <span>Location</span>
                </div>
              </label>
              <input
                type="text"
                value={location}
                onChange={e => setLocation(e.target.value)}
                placeholder="Add location"
                className={inputClasses}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                <div className="flex items-center gap-1.5">
                  <AlignLeft className="h-3.5 w-3.5" />
                  <span>Description</span>
                </div>
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Add description"
                rows={3}
                className={`${inputClasses} resize-none`}
              />
            </div>

            {/* Attendees / Guests */}
            <div className="bg-gray-50/80 rounded-xl p-3.5">
              <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
                  <Users className="h-3.5 w-3.5" />
                  <span>Guests</span>
                </div>
                <span className="text-[11px] text-gray-400">{attendees.length} invited</span>
              </div>

              {/* Add attendee input */}
              <div className="flex gap-2 mb-2">
                <input
                  type="email"
                  value={newAttendeeEmail}
                  onChange={e => setNewAttendeeEmail(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddAttendee(); } }}
                  placeholder="Add guest email"
                  className={`${inputClasses} flex-1`}
                />
                <button
                  type="button"
                  onClick={handleAddAttendee}
                  disabled={!newAttendeeEmail.trim()}
                  className="px-3 py-2 bg-blue-500 text-white rounded-xl text-xs font-medium hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add
                </button>
              </div>

              {/* Select existing customer */}
              <button
                type="button"
                onClick={() => setIsSelectCustomerOpen(true)}
                className="w-full mb-2.5 py-2 px-3 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200/60 rounded-xl transition-colors flex items-center justify-center gap-1.5"
              >
                <UserPlus className="h-3.5 w-3.5" />
                Select Customer
              </button>

              {/* Attendee list */}
              {attendees.length > 0 && (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {attendees.map(email => (
                    <div
                      key={email}
                      className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-[10px] font-semibold text-blue-600 uppercase">
                            {(attendeeNames[email] || email).charAt(0)}
                          </span>
                        </div>
                        <div className="min-w-0">
                          {attendeeNames[email] && (
                            <p className="text-xs font-medium text-gray-900 truncate">{attendeeNames[email]}</p>
                          )}
                          <p className={`text-xs truncate ${attendeeNames[email] ? 'text-gray-500' : 'text-gray-700'}`}>{email}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttendee(email)}
                        className="p-1 hover:bg-red-50 rounded-full transition-colors flex-shrink-0"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-red-400" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-4 border-t border-gray-100">
            <div className="flex gap-2.5">
              <button
                onClick={onClose}
                disabled={isSaving}
                className="flex-1 py-2.5 px-4 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={isSaving || !title.trim()}
                className="flex-1 py-2.5 px-4 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Event'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <SelectCustomerModal
        isOpen={isSelectCustomerOpen}
        onClose={() => setIsSelectCustomerOpen(false)}
        onSelect={handleSelectCustomer}
        baseZIndex={100300}
      />
    </>,
    document.body
  );
}
